import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  DollarSign,
  Users,
  Building2,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import { LoadingState } from '../../../components/AsyncStates';
import {
  getPayrollSummary,
  getSalaryRegister,
  getDepartmentCost,
  PayrollReportInput,
  PayrollSummaryView,
  SalaryRegisterView,
  DepartmentCostView,
} from '../../../api/Payroll/payrollReports';
import toast from 'react-hot-toast';

type ReportTab = 'summary' | 'register' | 'department';

export default function PayrollReports() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ReportTab>('summary');
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setDate(0);
    return d.toISOString().split('T')[0];
  });

  const [summaryData, setSummaryData] = useState<PayrollSummaryView[]>([]);
  const [registerData, setRegisterData] = useState<SalaryRegisterView[]>([]);
  const [departmentData, setDepartmentData] = useState<DepartmentCostView[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const input: PayrollReportInput = {
        fromDate,
        toDate,
        currencyId: 0,
      };

      if (activeTab === 'summary') {
        const result = await getPayrollSummary(input);
        if (result.errors.length === 0) {
          setSummaryData(result.payload);
        } else {
          toast.error(result.errors[0]?.message || t('payrollReports.error'));
        }
      } else if (activeTab === 'register') {
        const result = await getSalaryRegister(input);
        if (result.errors.length === 0) {
          setRegisterData(result.payload);
        } else {
          toast.error(result.errors[0]?.message || t('payrollReports.error'));
        }
      } else {
        const result = await getDepartmentCost(input);
        if (result.errors.length === 0) {
          setDepartmentData(result.payload);
        } else {
          toast.error(result.errors[0]?.message || t('payrollReports.error'));
        }
      }
      setHasLoaded(true);
    } catch {
      toast.error(t('payrollReports.error'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, fromDate, toDate, t]);

  const tabs: { key: ReportTab; label: string; icon: React.ElementType }[] = [
    { key: 'summary', label: t('payrollReports.summary'), icon: BarChart3 },
    { key: 'register', label: t('payrollReports.salaryRegister'), icon: Users },
    { key: 'department', label: t('payrollReports.departmentCost'), icon: Building2 },
  ];

  const formatCurrency = (val: number) =>
    val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-sm">
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t('payrollReports.title')}</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg mb-6 w-fit">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); setHasLoaded(false); }}
            data-testid={`tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg border border-border p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">
              {t('payrollReports.dateRange')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
              />
              <span className="text-muted-foreground text-sm">{t('common.to')}</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 border border-input bg-background rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
              />
            </div>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            data-testid="generate-report"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            {t('payrollReports.generate')}
          </button>
          {hasLoaded && (
            <div className="flex gap-2 ms-auto">
              <button
                className="flex items-center gap-2 px-3 py-2 border border-border rounded-md text-sm hover:bg-muted transition-colors"
                data-testid="export-pdf"
              >
                <Download className="w-4 h-4" />
                {t('payrollReports.exportPdf')}
              </button>
              <button
                className="flex items-center gap-2 px-3 py-2 border border-border rounded-md text-sm hover:bg-muted transition-colors"
                data-testid="export-excel"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {t('payrollReports.exportExcel')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingState title={t('Loading...')} size="md" />
      ) : !hasLoaded ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">{t('payrollReports.selectAndGenerate')}</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          {activeTab === 'summary' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-start p-3 font-medium text-muted-foreground">{t('payrollReports.department')}</th>
                    <th className="text-start p-3 font-medium text-muted-foreground">{t('payrollReports.month')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.grossTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.deductionsTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.netTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.overtimeTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.variablePayTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.headcount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-muted-foreground">
                        {t('payrollReports.noData')}
                      </td>
                    </tr>
                  ) : (
                    summaryData.map((row, i) => (
                      <tr key={i} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3">{row.departmentName}</td>
                        <td className="p-3">{row.month}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.grossTotal)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.deductionsTotal)}</td>
                        <td className="p-3 text-end font-mono font-medium">{formatCurrency(row.netTotal)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.overtimeTotal)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.variablePayTotal)}</td>
                        <td className="p-3 text-end">{row.headcount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'register' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-start p-3 font-medium text-muted-foreground">{t('payrollReports.employee')}</th>
                    <th className="text-start p-3 font-medium text-muted-foreground">{t('payrollReports.department')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.grossTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.deductionsTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.variablePayTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.overtimeTotal')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.adjustments')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.netTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {registerData.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-muted-foreground">
                        {t('payrollReports.noData')}
                      </td>
                    </tr>
                  ) : (
                    registerData.map((row, i) => (
                      <tr key={i} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3">{row.employeeName}</td>
                        <td className="p-3">{row.departmentName}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.grossSalary)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.deductions)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.variablePay)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.overtime)}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.adjustments)}</td>
                        <td className="p-3 text-end font-mono font-medium">{formatCurrency(row.netPay)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'department' && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-start p-3 font-medium text-muted-foreground">{t('payrollReports.department')}</th>
                    <th className="text-start p-3 font-medium text-muted-foreground">{t('payrollReports.month')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.totalCost')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.headcount')}</th>
                    <th className="text-end p-3 font-medium text-muted-foreground">{t('payrollReports.avgSalary')}</th>
                  </tr>
                </thead>
                <tbody>
                  {departmentData.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground">
                        {t('payrollReports.noData')}
                      </td>
                    </tr>
                  ) : (
                    departmentData.map((row, i) => (
                      <tr key={i} className="border-b border-border hover:bg-muted/30">
                        <td className="p-3">{row.departmentName}</td>
                        <td className="p-3">{row.month}</td>
                        <td className="p-3 text-end font-mono font-medium">{formatCurrency(row.totalCost)}</td>
                        <td className="p-3 text-end">{row.headcount}</td>
                        <td className="p-3 text-end font-mono">{formatCurrency(row.avgSalary)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
